/**
 * Sequencer runtime state — the PWA-side mirror of bridge `sequencer.state`
 * broadcasts. Components read from here to drive UI (armed glow, playhead
 * column, step pulse) without having to subscribe to the bridge themselves.
 *
 * Mirrors `macros/runtime.ts` in shape.
 */

import { createSignal, type Accessor } from 'solid-js';
import type { SequencerStateMessage } from '@opbuttons/schema';

export interface SequencerSnapshot {
  state: 'idle' | 'armed' | 'running' | 'killing';
  startBeat: number;
  lastFiredSteps: number[];
  /** Wall-clock ms at which this snapshot was received. Used by the step
   *  pulse animation to detect "fired in the last 120ms" and trigger a
   *  flash. */
  ts: number;
}

const [statesSignal, setStatesSignal] = createSignal<Record<string, SequencerSnapshot>>({});

export const sequencerStates: Accessor<Record<string, SequencerSnapshot>> = statesSignal;

/** Called directly by `opbridge/enginesStatePoll.ts` per poll tick. */
export function recordSequencerState(msg: SequencerStateMessage): void {
  setStatesSignal((prev) => ({
    ...prev,
    [msg.sequencerId]: {
      state: msg.state,
      startBeat: msg.startBeat,
      lastFiredSteps: msg.lastFiredSteps,
      ts: Date.now(),
    },
  }));
}
